import React, { useState } from "react";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";

// Course configuration mapping - defines all courses and their certificate details
const COURSE_CONFIG = {
    'free': {
        title: 'Free Peer Support Course',
        filePrefix: 'PilotHelper_Free_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'free-course': {
        title: 'Free Peer Support Course',
        filePrefix: 'PilotHelper_Free_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'college': {
        title: 'College Pilot Peer Support Course',
        filePrefix: 'PilotHelper_College_Certificate',
        pdfTemplate: 'PHCollegeCertificate.pdf'
    },
    'college-student': {
        title: 'College Pilot Peer Support Course',
        filePrefix: 'PilotHelper_College_Certificate',
        pdfTemplate: 'PHCollegeCertificate.pdf'
    },
    'flight-school': {
        title: 'Flight School Peer Support Course',
        filePrefix: 'PilotHelper_FlightSchool_Certificate',
        pdfTemplate: 'PHFlightSchoolCertificate.pdf'
    },
    'airline-pilot': {
        title: 'Airline Pilot Peer Support Course',
        filePrefix: 'PilotHelper_AirlinePilot_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'corporate-pilot': {
        title: 'Corporate Pilot Peer Support Course',
        filePrefix: 'PilotHelper_CorporatePilot_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'flight-attendant': {
        title: 'Flight Attendant Peer Support Course',
        filePrefix: 'PilotHelper_FlightAttendant_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'air-traffic-controller': {
        title: 'Air Traffic Controller Peer Support Course',
        filePrefix: 'PilotHelper_ATC_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'military-pilot': {
        title: 'Military Pilot Peer Support Course',
        filePrefix: 'PilotHelper_MilitaryPilot_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'aircraft-maintenance': {
        title: 'Aircraft Maintenance Technician Peer Support Course',
        filePrefix: 'PilotHelper_AircraftMaintenance_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'aircraft-dispatcher': {
        title: 'Aircraft Dispatcher Peer Support Course',
        filePrefix: 'PilotHelper_AircraftDispatcher_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    },
    'airport-operations': {
        title: 'Airport Operations Peer Support Course',
        filePrefix: 'PilotHelper_AirportOperations_Certificate',
        pdfTemplate: 'PHCertificate.pdf'
    }
};

// Default config for unknown course types
const DEFAULT_COURSE_CONFIG = {
    title: 'Peer Support Course',
    filePrefix: 'PilotHelper_Certificate',
    pdfTemplate: 'PHCertificate.pdf'
};

const CertificateGenerator = ({ user, onClose, courseType = 'free' }) => {
    const [certificateUrl, setCertificateUrl] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [certificateName, setCertificateName] = useState(user?.displayName || '');
    const [nameError, setNameError] = useState('');

    // Get course config or use default
    const courseConfig = COURSE_CONFIG[courseType] || DEFAULT_COURSE_CONFIG;

    const generateCertificate = async () => {
        // Validate name
        const trimmedName = certificateName.trim();
        if (!trimmedName) {
            setNameError('Please enter your name for the certificate');
            return;
        }
        if (trimmedName.length < 2) {
            setNameError('Name must be at least 2 characters');
            return;
        }
        setNameError('');
        setIsGenerating(true);
        try {
            // Load the appropriate certificate template based on course type
            const pdfFileName = courseConfig.pdfTemplate;

            // Fetch with more robust error handling
            const response = await fetch(`/assets/${pdfFileName}`, {
                method: 'GET',
                headers: {
                    'Cache-Control': 'no-cache',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to load PDF template: ${response.status} ${response.statusText}`);
            }

            const existingPdfBytes = await response.arrayBuffer();
            const pdfDoc = await PDFDocument.load(existingPdfBytes);
            const page = pdfDoc.getPages()[0];

            // Load font - using StandardFonts which should work everywhere
            const cursiveFont = await pdfDoc.embedFont(StandardFonts.CourierOblique);

            const { width, height } = page.getSize();
            const completionDate = new Date().toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });

            // Add user's name (use the custom name entered by user)
            const userName = certificateName.trim();

            // Adjust name placement (you may need to adjust these coordinates based on your cert.pdf layout)
            page.drawText(userName, {
                x: width / 2 - (userName.length * 10), // Dynamic centering based on name length
                y: height / 2 - 38,  // Adjust this based on your PDF layout
                size: 36,
                font: cursiveFont,
                color: rgb(0, 0, 0),
            });

            // Add completion date
            page.drawText(completionDate, {
                x: width - 270,  // Adjust based on your PDF layout
                y: 130,  // Adjust based on your PDF layout
                size: 18,
                color: rgb(0, 0, 0),
            });

            const modifiedPdfBytes = await pdfDoc.save();
            const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);

            setCertificateUrl(url);
            setIsGenerating(false);
        } catch (error) {
            console.error('Error generating certificate:', error);

            // More detailed error message
            let errorMessage = 'Failed to generate certificate. ';
            if (error.message.includes('Failed to load PDF template')) {
                errorMessage += 'Could not load the certificate template. Please check your internet connection and try again.';
            } else if (error.message.includes('font')) {
                errorMessage += 'There was an issue with font loading. Please try again.';
            } else {
                errorMessage += 'Please try again or contact support if the problem persists.';
            }

            alert(errorMessage + '\n\nError details: ' + error.message);
            setIsGenerating(false);
        }
    };

    const downloadCertificate = async () => {
        if (!certificateUrl) return;

        setIsDownloading(true);

        try {
            // Method 1: Try standard download (works in most browsers)
            const link = document.createElement('a');
            link.href = certificateUrl;
            const fileName = `${courseConfig.filePrefix}_${certificateName.trim().replace(/\s+/g, '_') || 'Certificate'}.pdf`;
            link.download = fileName;

            // Some browsers require the link to be in the document
            link.style.display = 'none';
            document.body.appendChild(link);

            // Trigger click
            link.click();

            // Clean up
            setTimeout(() => {
                document.body.removeChild(link);
                setIsDownloading(false);
            }, 100);

        } catch (error) {
            console.error('Download error:', error);

            // Fallback: Try opening in new window (mobile Safari, etc.)
            try {
                const newWindow = window.open(certificateUrl, '_blank');
                if (!newWindow) {
                    throw new Error('Popup blocked');
                }
            } catch (fallbackError) {
                console.error('Fallback download failed:', fallbackError);
                alert('Unable to download automatically. Please try:\n\n1. Right-click the preview above and select "Save"\n2. Or use a different browser\n3. Or disable your popup blocker');
            }

            setIsDownloading(false);
        }
    };


    // Function to send the certificate by email
    /* // Note: This is a placeholder function. Actual email sending requires backend support.
    const sendCertificateByEmail = async () => {
        if (!certificateUrl) return;

        try {
            // Convert blob to base64 for email
            const response = await fetch(certificateUrl);
            const blob = await response.blob();
            const reader = new FileReader();
            
            reader.onloadend = () => {
                // For now, we'll simulate email sending
                // In production, you'd send this to your backend
                console.log('Certificate ready for email:', {
                    to: user.email,
                    subject: 'Your PilotHelper Peer Support Certificate',
                    attachment: reader.result,
                    userName: user.displayName
                });
                
                // Simulate successful email
               // setEmailSent(true);
                //alert(`Certificate sent to ${user.email}! (Note: Email functionality requires backend setup)`);
            };
            
            reader.readAsDataURL(blob);
        } catch (error) {
            console.error('Error sending email:', error);
            alert('Failed to send email. Please download the certificate instead.');
        }
    };  */

    return (
        <>
            {/* Content spacer to prevent header/footer collision */}
            <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* This invisible content keeps the page structure intact */}
            </div>

            {/* Modal overlay */}
            <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="certificate-modal-title"
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050,
                    padding: '10px'
                }}
            >
            <div
                className="modal-content"
                style={{
                    width: '90%',
                    maxWidth: certificateUrl ? '700px' : '500px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}
            >
                <div className="modal-header" style={{ padding: '12px 16px' }}>
                    <h5 id="certificate-modal-title" className="modal-title">
                        <i className="fas fa-certificate text-warning me-2" aria-hidden="true"></i>
                        Your Certificate
                    </h5>
                    <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                </div>

                <div className="modal-body text-center" style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
                    <div className="mb-3">
                        <h4 style={{ fontSize: '1.5rem', marginBottom: '8px' }}>Congratulations! 🎉</h4>
                        <p className="text-muted" style={{ marginBottom: '0' }}>
                            You have successfully completed the <strong>{courseConfig.title}</strong>.
                        </p>
                    </div>

                    {!certificateUrl ? (
                        <div>
                            <p className="mb-3">Enter the name you want to appear on your certificate:</p>

                            {/* Name Input Field */}
                            <div className="mb-4">
                                <input
                                    type="text"
                                    className={`form-control form-control-lg text-center ${nameError ? 'is-invalid' : ''}`}
                                    placeholder="Enter your full name"
                                    value={certificateName}
                                    onChange={(e) => {
                                        setCertificateName(e.target.value);
                                        setNameError('');
                                    }}
                                    disabled={isGenerating}
                                    style={{ maxWidth: '400px', margin: '0 auto' }}
                                    aria-label="Full name for certificate"
                                    aria-describedby={nameError ? "cert-name-error" : "cert-name-hint"}
                                    aria-invalid={nameError ? "true" : "false"}
                                />
                                {nameError && (
                                    <div id="cert-name-error" className="invalid-feedback" style={{ display: 'block' }} role="alert">
                                        {nameError}
                                    </div>
                                )}
                                <small id="cert-name-hint" className="text-muted d-block mt-2">
                                    This name will appear on your official certificate
                                </small>
                            </div>

                            <button
                                className="btn btn-primary btn-lg"
                                onClick={generateCertificate}
                                disabled={isGenerating || !certificateName.trim()}
                                aria-busy={isGenerating}
                            >
                                {isGenerating ? (
                                    <>
                                        <i className="fas fa-spinner fa-spin me-2" aria-hidden="true"></i>
                                        Generating Certificate...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-certificate me-2" aria-hidden="true"></i>
                                        Generate Certificate
                                    </>
                                )}
                            </button>
                        </div>
                    ) : (
                        <div>
                            <div className="alert alert-success py-2 mb-3" role="status">
                                <i className="fas fa-check-circle me-2" aria-hidden="true"></i>
                                Your certificate has been generated successfully!
                            </div>

                            {/* Certificate Preview - smaller on mobile */}
                            <iframe
                                src={certificateUrl}
                                width="100%"
                                style={{
                                    border: '1px solid #ddd',
                                    borderRadius: '8px',
                                    height: '35vh',
                                    minHeight: '180px',
                                    maxHeight: '300px'
                                }}
                                title="Certificate Preview"
                            ></iframe>
                        </div>
                    )}
                </div>

                {/* Footer with action buttons - always visible */}
                <div className="modal-footer" style={{ padding: '12px 16px', flexDirection: 'column', gap: '8px' }}>
                    {certificateUrl ? (
                        <>
                            <div className="d-flex gap-2 w-100 justify-content-center flex-wrap">
                                <button
                                    className="btn btn-success"
                                    onClick={downloadCertificate}
                                    disabled={isDownloading}
                                    aria-busy={isDownloading}
                                >
                                    {isDownloading ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin me-2" aria-hidden="true"></i>
                                            Downloading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-download me-2" aria-hidden="true"></i>
                                            Download Certificate
                                        </>
                                    )}
                                </button>
                                <a
                                    href={certificateUrl}
                                    download={`${courseConfig.filePrefix}_${certificateName.trim().replace(/\s+/g, '_') || 'Certificate'}.pdf`}
                                    className="btn btn-outline-primary"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <i className="fas fa-external-link-alt me-2" aria-hidden="true"></i>
                                    Open in New Tab
                                </a>
                            </div>
                            <button type="button" className="btn btn-primary" onClick={onClose}>
                                Close
                            </button>
                        </>
                    ) : (
                        <button type="button" className="btn btn-primary" onClick={onClose}>
                            Close
                        </button>
                    )}
                </div>
            </div>
        </div>
        </>
    );
};

export default CertificateGenerator;